import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '../../components/layout/Header';
import { Button } from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { AppIcon, type IconName } from '../../components/icons/AppIcon';
import { useIsPartner } from './hooks/useIsPartner';
import { notify } from '../../utils/toast';
import { isUnauthenticatedClientError } from '../../lib/supabase';
import { cn } from '@/lib/utils';
import {
  fetchPartnerDashboard,
  startPartnerConnectOnboarding,
  PartnerApiError,
  type PartnerDashboardPayload,
} from '../../services/modules/partners/partnerApi';

const NUM = 'font-mono font-semibold tabular-nums tracking-tight';
const MONEY = 'font-mono font-medium tabular-nums tracking-tight';

function formatBrl(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
}

function referralStatusBadge(status: string | null): { label: string; className: string } {
  if (status === 'active') return { label: 'Ativo', className: 'badge badge-sm badge-success' };
  if (status === 'trialing') return { label: 'Trial', className: 'badge badge-sm badge-info' };
  if (status === 'past_due') return { label: 'Inadimplente', className: 'badge badge-sm badge-warning' };
  if (status === 'canceled') return { label: 'Cancelado', className: 'badge badge-sm badge-danger' };
  return { label: status || '—', className: 'badge badge-sm badge-neutral' };
}

function commissionStatusBadge(status: string): { label: string; className: string } {
  const normalized = status.toLowerCase();
  if (normalized === 'paid' || normalized === 'pago') {
    return { label: status, className: 'badge badge-sm badge-success' };
  }
  if (normalized === 'available' || normalized === 'disponível' || normalized === 'disponivel') {
    return { label: status, className: 'badge badge-sm badge-brand' };
  }
  if (normalized === 'pending' || normalized === 'pendente') {
    return { label: status, className: 'badge badge-sm badge-warning' };
  }
  if (normalized === 'processing' || normalized === 'processando') {
    return { label: 'Processando', className: 'badge badge-sm badge-info' };
  }
  if (normalized === 'reversed' || normalized === 'failed') {
    return { label: status, className: 'badge badge-sm badge-danger' };
  }
  return { label: status, className: 'badge badge-sm badge-neutral' };
}

type Kpi = {
  label: string;
  value: string;
  accent: 'brand' | 'success' | 'warning';
  icon: IconName;
};

export function PartnerDashboardPage() {
  const { partner: profile } = useIsPartner();
  const [data, setData] = useState<PartnerDashboardPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = await fetchPartnerDashboard();
      setData(payload);
    } catch (e) {
      // Sessão já foi limpa em getValidAccessToken — AuthContext notifica uma vez.
      if (isUnauthenticatedClientError(e)) return;
      if (e instanceof PartnerApiError && e.isForbidden) {
        notify.error('Sem permissão para o portal parceiro.');
        return;
      }
      notify.error(e instanceof Error ? e : 'Erro ao carregar portal parceiro');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCopyLink = async () => {
    const link =
      data?.referralLink ||
      (profile?.code ? `${window.location.origin}/registro?ref=${encodeURIComponent(profile.code)}` : '');
    if (!link) return;
    await navigator.clipboard.writeText(link);
    notify.success('Link copiado');
  };

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const returnUrl = `${window.location.origin}/parceiro`;
      const url = await startPartnerConnectOnboarding(returnUrl);
      window.location.href = url;
    } catch (e) {
      // Sessão já foi limpa em getValidAccessToken — AuthContext notifica uma vez.
      if (isUnauthenticatedClientError(e)) return;
      notify.error(e instanceof Error ? e.message : 'Erro ao conectar Stripe');
    } finally {
      setConnectLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">Carregando portal parceiro</span>
      </div>
    );
  }

  const partner = data?.partner ?? profile;
  if (!partner) return null;

  const referralLink =
    data?.referralLink ||
    (partner.code ? `${window.location.origin}/registro?ref=${encodeURIComponent(partner.code)}` : '');
  const stats = data?.stats;
  const referrals = data?.referrals ?? [];
  const commissions = data?.commissions ?? [];

  if (partner.status === 'pending') {
    return (
      <article className="mx-auto flex w-full max-w-2xl flex-col gap-s4">
        <PageHeader
          title="Programa de Parceiros"
          subtitle="Sua candidatura está em análise"
          titleClassName="text-xl font-semibold tracking-tight text-default"
          subtitleClassName="text-[13px] text-muted"
          hideBackButton
        />
        <aside
          className="flex items-start gap-s3 rounded-lg border border-info/25 bg-info-bg px-s4 py-s3 text-info-text"
          role="status"
        >
          <AppIcon name="clock" className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p className="text-sm leading-relaxed">
            Assim que aprovada, você receberá seu link de indicação aqui.
          </p>
        </aside>
      </article>
    );
  }

  if (partner.status !== 'active') {
    return (
      <article className="mx-auto flex w-full max-w-2xl flex-col gap-s4">
        <PageHeader
          title="Programa de Parceiros"
          subtitle="Conta de parceiro indisponível"
          titleClassName="text-xl font-semibold tracking-tight text-default"
          subtitleClassName="text-[13px] text-muted"
          hideBackButton
        />
        <aside
          className="flex items-start gap-s3 rounded-lg border border-warning/30 bg-warning-bg px-s4 py-s3 text-warning-text"
          role="status"
        >
          <AppIcon name="alert" className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p className="text-sm leading-relaxed">
            Entre em contato com o suporte Adlitics para mais informações.
          </p>
        </aside>
      </article>
    );
  }

  const kpis: Kpi[] = [
    {
      label: 'Indicações',
      value: String(stats?.referralCount ?? 0),
      accent: 'brand',
      icon: 'users',
    },
    {
      label: 'Clientes ativos',
      value: String(stats?.activeReferrals ?? 0),
      accent: 'success',
      icon: 'chart',
    },
    {
      label: 'Comissão do mês',
      value: formatBrl(stats?.monthCommissionCents ?? 0),
      accent: 'brand',
      icon: 'calendar',
    },
    {
      label: 'Saldo disponível',
      value: formatBrl(stats?.availableBalanceCents ?? 0),
      accent: 'warning',
      icon: 'wallet',
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-s5 md:gap-s6">
      <PageHeader
        title="Programa de Parceiros"
        subtitle="Indique clientes e acompanhe suas comissões"
        titleClassName="text-xl font-semibold tracking-tight text-default"
        subtitleClassName="text-[13px] text-muted"
        hideBackButton
      />

      {!partner.crmAccessGranted ? (
        <aside
          className="flex items-start gap-s3 rounded-lg border border-warning/30 bg-warning-bg px-s4 py-s3 text-warning-text"
          role="status"
        >
          <AppIcon name="gift" className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Acesso ao CRM liberado após a 1ª indicação paga</p>
            <p className="mt-s1 text-[13px] leading-relaxed opacity-90">
              Quando seu primeiro cliente indicado pagar, você ganha acesso gratuito ao CRM Adlitics (plano basic).
            </p>
          </div>
        </aside>
      ) : null}

      <section aria-label="Indicadores" className="grid grid-cols-1 gap-s3 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <article key={kpi.label} className="kpi-card min-w-0">
            <div
              className={cn(
                'kpi-accent',
                kpi.accent === 'success' && 'kpi-accent--success',
                kpi.accent === 'warning' && 'kpi-accent--warning',
              )}
              aria-hidden
            />
            <header className="flex min-w-0 items-start justify-between gap-s2">
              <h3 className="kpi-label mb-s2 min-w-0 truncate">{kpi.label}</h3>
              <div
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-lg',
                  kpi.accent === 'success' && 'bg-success-bg text-success-text',
                  kpi.accent === 'warning' && 'bg-warning-bg text-warning-text',
                  kpi.accent === 'brand' && 'bg-brand-50 text-brand-600',
                )}
                aria-hidden
              >
                <AppIcon name={kpi.icon} className="size-4" />
              </div>
            </header>
            <p
              className={cn(
                NUM,
                'text-[22px] leading-tight text-primary md:text-[28px]',
                kpi.accent === 'success' && 'text-success-text',
                kpi.accent === 'warning' && 'text-warning-text',
              )}
            >
              {kpi.value}
            </p>
          </article>
        ))}
      </section>

      <section className="card card-pad-lg" aria-labelledby="partner-referral-link-title">
        <header>
          <h2 id="partner-referral-link-title" className="card-title">
            Seu link de indicação
          </h2>
          <p className="card-sub">
            Comissão de{' '}
            <span className={cn(NUM, 'text-primary')}>
              {(partner.commissionRate * 100).toFixed(0)}%
            </span>{' '}
            por até 12 meses por cliente.
          </p>
        </header>
        <div className="mt-s4 flex flex-col gap-s2 sm:flex-row sm:items-center">
          <label htmlFor="partner-referral-link" className="sr-only">
            Link de indicação
          </label>
          <input
            id="partner-referral-link"
            readOnly
            value={referralLink}
            className="input min-w-0 flex-1 font-mono text-[13px] tabular-nums"
          />
          <Button
            type="button"
            className="min-h-11 w-full shrink-0 touch-manipulation sm:min-h-9 sm:w-auto"
            onClick={() => void handleCopyLink()}
            disabled={!referralLink}
          >
            Copiar link
          </Button>
        </div>
        {partner.code ? (
          <p className="mt-s3 text-xs text-muted">
            Código:{' '}
            <span className="font-mono font-medium tabular-nums text-secondary">{partner.code}</span>
          </p>
        ) : null}
      </section>

      {!partner.connectOnboardingComplete ? (
        <section
          className="rounded-lg border border-brand-200 bg-gradient-to-br from-brand-50 to-card p-s4 shadow-xs md:p-s6"
          aria-labelledby="partner-stripe-title"
        >
          <header>
            <h2 id="partner-stripe-title" className="card-title">
              Receba suas comissões
            </h2>
            <p className="card-sub">
              Conecte sua conta Stripe para receber payouts automáticos (mínimo R$&nbsp;100).
            </p>
          </header>
          <Button
            type="button"
            className="mt-s4 min-h-11 w-full touch-manipulation sm:w-auto"
            onClick={() => void handleConnect()}
            disabled={connectLoading}
          >
            {connectLoading ? 'Abrindo Stripe…' : 'Conectar conta Stripe'}
          </Button>
        </section>
      ) : null}

      <section className="card overflow-hidden" aria-labelledby="partner-referrals-title">
        <header className="flex flex-col gap-s3 border-b border-subtle px-s4 py-s4 sm:flex-row sm:items-center sm:justify-between md:px-s5">
          <div className="min-w-0">
            <h2 id="partner-referrals-title" className="card-title mb-0">
              Indicações
            </h2>
            <p className="card-sub mt-s1">Clientes que entraram pelo seu link</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-auto"
            onClick={() => void load()}
          >
            Atualizar
          </Button>
        </header>

        {referrals.length === 0 ? (
          <div className="flex flex-col items-center px-s4 py-s10 text-center md:py-s12">
            <div
              className="mb-s4 flex size-[52px] items-center justify-center rounded-xl bg-surface-2 text-muted"
              aria-hidden
            >
              <AppIcon name="users" className="size-5" />
            </div>
            <p className="text-sm font-medium text-default">Nenhuma indicação ainda</p>
            <p className="mt-s1 max-w-sm text-[13px] text-muted">
              Compartilhe seu link de indicação para começar a acompanhar clientes aqui.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl min-w-[520px]">
              <caption className="sr-only">Lista de indicações do parceiro</caption>
              <thead>
                <tr>
                  <th scope="col">Empresa</th>
                  <th scope="col">Status</th>
                  <th scope="col">Comissão até</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((row) => {
                  const badge = referralStatusBadge(row.subscriptionStatus);
                  return (
                    <tr key={row.id}>
                      <td className="font-medium text-default">{row.organizationName}</td>
                      <td>
                        <span className={badge.className}>{badge.label}</span>
                      </td>
                      <td className={cn(MONEY, 'text-secondary')}>
                        {new Date(row.commissionEndsAt).toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card overflow-hidden" aria-labelledby="partner-commissions-title">
        <header className="border-b border-subtle px-s4 py-s4 md:px-s5">
          <h2 id="partner-commissions-title" className="card-title mb-0">
            Extrato de comissões
          </h2>
          <p className="card-sub mt-s1">Valores gerados pelas assinaturas indicadas</p>
        </header>

        {commissions.length === 0 ? (
          <div className="flex flex-col items-center px-s4 py-s10 text-center md:py-s12">
            <div
              className="mb-s4 flex size-[52px] items-center justify-center rounded-xl bg-surface-2 text-muted"
              aria-hidden
            >
              <AppIcon name="wallet" className="size-5" />
            </div>
            <p className="text-sm font-medium text-default">Nenhuma comissão registrada</p>
            <p className="mt-s1 max-w-sm text-[13px] text-muted">
              As comissões aparecem aqui quando um cliente indicado efetua o pagamento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl min-w-[560px]">
              <caption className="sr-only">Extrato de comissões do parceiro</caption>
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col" className="text-right">
                    Valor pago
                  </th>
                  <th scope="col" className="text-right">
                    Comissão
                  </th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((row) => {
                  const badge = commissionStatusBadge(row.status);
                  return (
                    <tr key={row.id}>
                      <td className={cn(MONEY, 'text-secondary')}>
                        {new Date(row.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className={cn(MONEY, 'text-right text-secondary')}>
                        {formatBrl(row.amountPaid)}
                      </td>
                      <td className={cn(NUM, 'text-right text-primary')}>
                        {formatBrl(row.commissionAmount)}
                      </td>
                      <td>
                        <span className={badge.className}>{badge.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
